<template>
  <div>
    <div
      :class="[
        'logo-container',
        'input-grey',
        { 'custom-error-border': validator && validator.$error },
      ]"
    >
      <v-layout v-if="uploadInProgress" wrap fill-height justify-center align-center>
        <v-flex shrink>
          <v-progress-circular color="primary" indeterminate :size="55" />
        </v-flex>
      </v-layout>
      <div v-else>
        <drop style="height: 100%" @click="handleDrop" @drop="handleDrop">
          <v-layout
            v-if="!value"
            style="height: 100%"
            justify-center
            align-center
            column
            class="text-container"
            @click="handleOpenFSPicker"
          >
            <v-flex shrink>
              <v-icon color="primary" large> photo</v-icon>
            </v-flex>
            <v-flex shrink>
              <span class="logo-title">{{ adminText(&quot;shellAll.add_logo&quot;, &quot;Add logo&quot;) }}</span>
            </v-flex>
            <v-flex shrink class="text-center description">
              <span>{{ adminText(&quot;shellAll.image_with_1_1_aspect_ratio_and_no_larger_than_5_mb&quot;, &quot;Image with 1:1 aspect ratio and no larger than 5 MB&quot;) }}
                <div>{{ adminText(&quot;shellAll.is&quot;, &quot;is&quot;) }}</div>
                {{ adminText(&quot;shellAll.recommended&quot;, &quot;recommended.&quot;) }}</span>
            </v-flex>
          </v-layout>
          <v-layout
            v-else
            :style="{ backgroundImage: 'url(' + value + ')' }"
            justify-center
            align-end
            class="image-container"
          >
            <div v-if="!disabled" style="display: flex">
              <v-flex shrink class="p-0 mb-12 image-button">
                <v-btn icon class="size-32 ml-0 primary" @click.stop="handleOpenFSPicker">
                  <v-icon>photo_library</v-icon>
                </v-btn>
              </v-flex>
              <v-flex shrink class="p-0 mb-12 image-button">
                <v-btn icon class="size-32 ml-0 error" @click.stop="removeImage">
                  <v-icon>delete</v-icon>
                </v-btn>
              </v-flex>
            </div>
          </v-layout>
        </drop>
      </div>
    </div>
    <div
      style="text-align: center"
      :class="[
        'custom-error-message',
        'text-align',
        { 'error--text': validator && validator.$error },
      ]"
    >
      <span> {{ getErrors(validator)[0] }} &nbsp;</span>
    </div>
    <cropper-dialog
      :aspect-ratio="1"
      :image="dataURLImage"
      @dismissed="removeImage"
      @cropped="uploadLogo($event)"
    />
    <hidden-file-input ref="file-input" max-file-size-mb="5" @file="processFile($event)" />
  </div>
</template>

<script>
import AdminContext from '@/admin-i18n/context'
import HiddenFileInput from './../hidden-file-input'
import CropperDialog from './../dialogs/cropper-dialog'
import {EventBus} from '@/plugins/event-bus.js'

import {
  fileToDataUrl,
  getUploadOptions,
  uploadImage,
  defaultMessages,
  getValidationErrors,
} from '@/utils'
import { UploadPaths } from '@/types'

export default {
  mixins: [AdminContext],
  name: 'logo-field',
  components: {
    HiddenFileInput,
    CropperDialog,
  },
  props: {
    validator: {
      type: Object,
      default: null,
    },
    uploadOptions: {
      type: Object,
      default: () => ({}),
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    errorMessages: {
      type: Object,
      default: null,
    },
    value: {
      type: [Date, String],
      default: null,
    },
  },
  data() {
    return {
      dataURLImage: null,
      uploadInProgress: false,
    }
  },
  methods: {
    handleOpenFSPicker() {
      !this.disabled && this.openFSPicker()
    },
    removeImage() {
      if (this.disabled) {
        return
      }
      this.dataURLImage = null
      this.$emit('input', null)
      this.validator && this.validator.$touch()
    },

    handleDrop(data, event) {
      if (this.disabled) {
        return
      }
      event.preventDefault()
      const {
        dataTransfer: { files },
      } = event
      this.processFile(files[0])
    },
    async processFile(file) {
      if (this.dataURLImage) {
        this.removeImage()
      }
      this.dataURLImage = await fileToDataUrl(file)
    },
    getErrors: function (field) {
      return getValidationErrors({
        ...this.adminValidationMessages(defaultMessages),
        ...this.adminFieldValidationMessages(this.errorMessages),
      })(field)
    },
    async uploadLogo(dataURL) {
      this.uploadInProgress = true
      EventBus.$emit('upload-in-progress', this.uploadInProgress)
      const uploadPath =
        this.uploadOptions && this.uploadOptions.path
          ? this.uploadOptions.path
          : UploadPaths.UNGROUPED
      const { file, path } = await getUploadOptions(dataURL, uploadPath, {
        width: 300,
        height: 300,
      })
      const url = await uploadImage(file, path)
      this.$emit('input', url)
      this.uploadInProgress = false
      EventBus.$emit('upload-in-progress', this.uploadInProgress)
      this.validator && this.validator.$touch()
    },
  },
}
</script>

<style scoped>
.logo-title {
  font-family: Raleway, sans-serif;
  font-size: 18px;
  font-weight: bold;
  line-height: 1.33;
  color: #0277bd;
  margin: 8px 0 4px;
  display: block;
}

.description {
  font-family: 'Source Sans Pro', sans-serif;
  font-size: 12px;
  line-height: 1.33;
  text-align: center;
  color: #0277bd;
}

.logo-container {
  height: 176px;
  width: 176px;
  border-radius: 88px;
  border: 2px dashed #cee4f2;
  cursor: pointer;
  background: white !important;
}

.text-container {
  padding: 12px;
}

.logo-container:hover {
  background-color: rgba(2, 119, 189, 0.2) !important;
}

.image-container {
  height: 176px;
  width: 176px;
  position: relative;
  border-radius: 88px;
  background-size: cover;
}

.image-container::after {
  content: '';
  position: absolute;
  height: 176px;
  width: 176px;
  border-radius: 88px;
  background: rgba(33, 33, 33, 0.43);
}

.image-button {
  position: relative;
  z-index: 1;
}

.image-button >>> .v-icon {
  font-size: 20px;
}
</style>
